import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import {
  AUS_ABN,
  NAME_COMPANY_REGEX,
  NAME_REGEX,
  NZL_NZBN_NUMBER_REGEX,
  REGISTER_OFFICE_REGEX,
} from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { countriesGroup1, countryOptions } from '@/modules/entity/static/countries.ts';
import { UsaPartnershipTypesOptions } from '@/modules/entity/static/usa_partnership_types.ts';
import { PartnershipEntityDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Button, Select, TextInputField } from 'ui';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { CountryCodeEnum } from 'api';

export const validationSchemaPartnership = yup.object<PartnershipEntityDetails>({
  country: yup.string().trim().required('This field is required'),
  abn: yup
    .string()
    .trim()
    .matches(AUS_ABN, 'Incorrect ABN, example of correct one: 12345678901')
    // .test('is-valid-abn', 'Please enter a valid ABN', value => {
    //   if (!value) return true; // Allow nullable (optional) values
    //
    //   return isValidABN(value);
    // })
    .when('country', ([country], schema) => {
      if (country === 'AUS') {
        return schema.required('This field is required');
      }

      return schema;
    }),
  nzbn: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'NZL') {
        return schema
          .matches(NZL_NZBN_NUMBER_REGEX, 'Incorrect NZBN, example of correct one: 6-13 digits')
          .required('This field is required');
      }

      return schema;
    }),
  entity_number: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'USA' || countriesGroup1.includes(country)) {
        return schema.required('This field is required');
      }
      return schema;
    }),
  partnership_type: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'USA') {
        return schema.required('This field is required');
      }
      return schema;
    }),
  registered_office: yup
    .string()
    .matches(
      REGISTER_OFFICE_REGEX,
      'Incorrect address, example of correct one: 111/11 Name Street, North Sydney NSW 1111',
    )
    .when('country', ([country], schema) => {
      if (country === 'USA') {
        return schema.required('This field is required');
      }
      return schema;
    }),
  principal_place_of_business: yup.string().trim().required('This field is required'),
  stakeholders: yup
    .array()
    .of(
      yup.object({
        first_name: yup
          .string()
          .matches(
            NAME_REGEX,
            'Invalid format, only allowed: letters, apostrophes, spaces, hyphens',
          )
          .required('This field is required'),
        last_name: yup
          .string()
          .matches(
            NAME_REGEX,
            'Invalid format, only allowed: letters, apostrophes, spaces, hyphens',
          )
          .required('This field is required'),
      }),
    )
    .nonNullable(`At least 1 stakeholder is required`)
    .min(1, `At least 1 stakeholder is required`),
  company_name: yup
    .string()
    .trim()
    .matches(
      NAME_COMPANY_REGEX,
      'Invalid format, only allowed: letters, numbers, spaces, apostrophes, hyphens, slashes and #',
    )
    .required('This field is required'),
});

export const initialValuesPartnershipDetails: PartnershipEntityDetails = {
  country: CountryCodeEnum.AUS,
  abn: '',
  nzbn: '',
  registered_office: '',
  principal_place_of_business: '',
  stakeholders: [],
  entity_number: '',
  partnership_type: '',
  company_name: '',
};

export const PartnershipDetails = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const getInitialValues = () => {
    const entityDetails = data?.entity.partnership_entity_details;
    return {
      ...initialValuesPartnershipDetails,
      country: data?.entity?.country || initialValuesPartnershipDetails?.country,
      ...entityDetails,
      stakeholders:
        (entityDetails?.stakeholders || [])?.map(item => ({
          ...item,
          type: item.company_name ? 'company' : ('individual' as 'company' | 'individual'),
          index: item.index || uuidv4(),
        })) || initialValuesPartnershipDetails?.stakeholders,
    };
  };

  const formik = useFormik<PartnershipEntityDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaPartnership,
    initialValues: getInitialValues(),
    onSubmit: async ({ company_name, ...details }) => {
      if (formik.dirty) {
        await updateEntity({
          ...data?.entity,
          entity_name: company_name,
          partnership_entity_details: {
            ...data?.entity.partnership_entity_details,
            ...details,
            company_name,
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const changeCountry = (value: string) => {
    formik.setFieldValue('country', value);
    formik.setFieldError('country', '');

    formik.setFieldValue('entity_number', '');
    formik.setFieldError('entity_number', '');
    formik.setFieldValue('nzbn', '');
    formik.setFieldError('nzbn', '');
    formik.setFieldValue('abn', '');
    formik.setFieldError('abn', '');
    formik.setFieldValue('registered_office', '');
    formik.setFieldError('registered_office', '');
    formik.setFieldValue('partnership_type', '');
    formik.setFieldError('partnership_type', '');
  };
  const handleDeleteStakeholder = (index: string) => {
    formik.setFieldValue('stakeholders', [
      ...(formik.values.stakeholders || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddStakeholder = () => {
    formik.setFieldValue('stakeholders', [
      ...(formik.values.stakeholders || []),
      { type: 'individual', first_name: '', last_name: '', index: uuidv4() },
    ]);
    formik.setFieldError('stakeholders', '');
  };

  const handleChangeSelectPartnership = (value: string | null) => {
    formik.setFieldValue(`partnership_type`, value);
    formik.setFieldError('partnership_type', '');
  };

  useEffect(() => {
    console.log('errors', formik.errors);
  }, [formik.errors]);

  return (
    <OnboardingWrapper
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading}
      title="Partnership details"
    >
      <>
        <Select
          isClearable
          label="Country of Formation, Incorporation or Registration"
          options={countryOptions}
          value={formik.values.country}
          onChange={value => changeCountry(value || '')}
          error={formik.errors?.country}
        />
        <TextInputField label="Partnership name" name="company_name" />
        {formik.values.country === 'AUS' && <TextInputField label="abn" name="abn" />}
        {(formik.values.country === 'USA' ||
          countriesGroup1.includes(formik.values.country ?? '')) && (
          <TextInputField label="entity number" name="entity_number" />
        )}
        {formik.values.country === 'NZL' && <TextInputField label="NZBN" name="nzbn" />}
        {formik.values.country === 'USA' && (
          <TextInputField
            label="Registered Office Address"
            name="registered_office"
            placeholder="111/11 Name Street, North Sydney NSW 1111"
          />
        )}
        {formik.values.country === 'USA' && (
          <Select
            name="partnership_type"
            label="Partnership Type"
            options={UsaPartnershipTypesOptions}
            value={formik.values.partnership_type}
            onChange={handleChangeSelectPartnership}
            error={formik.errors.partnership_type}
          />
        )}
        <TextInputField label="Principal Place Of Business" name="principal_place_of_business" />

        <div className="w-full flex flex-col space-y-6">
          <div className="flex justify-between items-center w-full">
            <span className="text-[16px] leading-6 font-[500] font-[Inter]">
              Tell us about your stakeholders
            </span>
            <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddStakeholder}>
              <img src={PlusBlueIcon} alt="Loading" />
              <span className="text-[14px] leading-[22px]">Add new stakeholder</span>
            </Button>
          </div>
          {(formik.values.stakeholders?.length ?? 0) > 0 && (
            <div className="w-full flex flex-col gap-6">
              {formik.values.stakeholders?.map((item, index) => {
                return (
                  <div key={'stakeholders_' + index} className="w-full flex flex-col gap-4">
                    <div className="w-full flex justify-between items-center">
                      <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                        Stakeholder #{index + 1}
                      </span>
                      <div className="flex gap-4 items-center select-none">
                        <span className="flex gap-4 items-center"></span>

                        <img
                          onClick={() => {
                            handleDeleteStakeholder(item.index);
                          }}
                          className="cursor-pointer hover:opacity-80"
                          src={TrashIcon}
                          alt="Loading..."
                        />
                      </div>
                    </div>

                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                      <TextInputField
                        label="first name"
                        name={`stakeholders[${index}].first_name`}
                      />
                      <TextInputField label="last name" name={`stakeholders[${index}].last_name`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {typeof formik.errors?.stakeholders === 'string' && formik.errors?.stakeholders && (
            <span className="text-sm text-red-500">{formik.errors?.stakeholders}</span>
          )}
        </div>
      </>
    </OnboardingWrapper>
  );
};
